/**
 * Acme demo · station 4 — "Build the handover process by describing it."
 *
 * The presenter types "create a process to control cross-site handovers". This plays
 * the Builder conversation — which handovers start it, which systems it touches
 * (multi-select read from the org graph), the checklist automation it found, who
 * approves a cross-site re-cut (free text), auto-recut policy — and assembles the
 * process ON THE CANVAS as each answer lands. It then runs a test pass and deploys:
 * the deploy lands the library row + detail in the store and keys the doc to the open
 * canvas, so the graph the Wizard just built IS the deployed process.
 *
 * Nothing here is in the baseline seed — acme-pr04 exists only in the asset until this
 * scenario deploys it, and reset removes it again.
 *
 * Stage 3 deliberately lands the tracker re-cut with its guard STRIPPED, and the
 * approval gate re-lands it WITH the guard, so cause and effect read together on
 * screen: the guard appears because the approver was named.
 */
import type { ActivityNode, Connection, Milestone, Stage } from '@wand/shared';
import type { WizardCanvasPatch } from '../../gateway/wizard.js';
import type { DeployProcessPayload, Scenario } from '../director.js';

/**
 * Nothing here is imported statically. The authored payload is ~20 kB and the demo
 * lazy chunk is capped at 60 kB gz by CI with little room to spare — importing it
 * statically measured 59.7 kB, i.e. 0.3 kB of headroom, which one prose edit would
 * spend. `canvas` and `deployProcess` both accept a loader (see `Lazy` in
 * director.ts), so this costs the demo chunk nothing but the copy below.
 */
const loadAsset = (): Promise<DeployProcessPayload> =>
  import('../assets/acme-handover-process.json').then(
    (m) => m.default as unknown as DeployProcessPayload,
  );

type PatchKey = 'NAME' | 'TRIGGERS' | 'STAGE1' | 'STAGE2' | 'STAGE3' | 'APPROVAL' | 'STAGE4';

/**
 * Slice the authored graph into the increments the conversation streams. Each slice
 * carries the connections whose BOTH endpoints have now appeared, so edges
 * materialize exactly when they can render — which means the slices are
 * order-dependent and computed once, together.
 */
async function buildPatches(): Promise<Record<PatchKey, WizardCanvasPatch>> {
  const DOC = (await loadAsset()).doc;
  const seen = new Set<string>();
  const usedConns = new Set<string>();
  const slice = (p: Omit<WizardCanvasPatch, 'conns'>): WizardCanvasPatch => {
    const els: { id: string }[] = [
      ...(p.triggers ?? []),
      ...(p.stages ?? []),
      ...(p.nodes ?? []),
      ...(p.milestones ?? []),
    ];
    for (const el of els) seen.add(el.id);
    const conns: Connection[] = DOC.conns.filter(
      (c) => !usedConns.has(c.id) && seen.has(c.fromId) && seen.has(c.toId),
    );
    for (const c of conns) usedConns.add(c.id);
    return { ...p, conns };
  };
  const stage = (id: string): Stage => DOC.stages.find((s) => s.id === id)!;
  const nodesIn = (sid: string, omit: string[] = []): ActivityNode[] =>
    DOC.nodes.filter((n) => n.stageId === sid && !omit.includes(n.id));
  const node = (id: string): ActivityNode => DOC.nodes.find((n) => n.id === id)!;
  const milestone = (id: string): Milestone => DOC.milestones.find((m) => m.id === id)!;

  return {
    NAME: slice({ name: DOC.name, agent: DOC.agent }),
    TRIGGERS: slice({ triggers: DOC.triggers }),
    STAGE1: slice({ stages: [stage('acme-pr04-s1')], nodes: nodesIn('acme-pr04-s1') }),
    STAGE2: slice({
      stages: [stage('acme-pr04-s2')],
      nodes: nodesIn('acme-pr04-s2'),
      milestones: [milestone('acme-pr04-m1')],
    }),
    // The human gate (a9) is held back so it arrives together with the write it
    // guards, and a10 lands here with its guard stripped so the guard's arrival
    // reads as a consequence of naming the approver.
    STAGE3: slice({
      stages: [stage('acme-pr04-s3')],
      nodes: nodesIn('acme-pr04-s3', ['acme-pr04-a9']).map((n) =>
        n.id === 'acme-pr04-a10' ? { ...n, guards: [] } : n,
      ),
    }),
    APPROVAL: slice({ nodes: [node('acme-pr04-a9'), node('acme-pr04-a10')] }),
    STAGE4: slice({
      stages: [stage('acme-pr04-s4')],
      nodes: nodesIn('acme-pr04-s4'),
      milestones: [milestone('acme-pr04-m2')],
      objective: DOC.objective ?? null,
      metrics: DOC.metrics ?? [],
      // Process-level fields travel with the last increment, so the first edit
      // after the deploy cannot autosave empty ones over the document.
      vars: DOC.vars,
      scope: DOC.scope,
      departments: DOC.departments ?? [],
    }),
  };
}

let patches: Promise<Record<PatchKey, WizardCanvasPatch>> | null = null;
/** Lazy, memoised, order-preserving access to one assembly increment. */
const P = (k: PatchKey) => async () => {
  if (!patches) patches = buildPatches();
  return (await patches)[k];
};

export const acmeHandoverProcess: Scenario = {
  id: 'acme-handover-process',
  orgId: 'acme',
  // findScenario keeps hyphens in its normalisation, so both forms are registered.
  keywords: [
    'cross-site handover',
    'cross site handover',
    'handover control',
    'handover process',
    'control handovers',
    'handover',
  ],
  steps: [
    {
      do: 'say',
      text: "Good one — a handover is where work changes hands and nobody owns it for a few hours. I'll scaffold a process that verifies one before the receiving site opens, and stops at a human when the fix moves work between sites. A few questions so I wire it right.",
      delay: 500,
    },
    {
      do: 'canvas',
      patch: P('NAME'),
      summary: 'Named it Cross-Site Handover Control · @Handover Control set as the driver',
      delay: 700,
    },
    {
      do: 'insight',
      title: 'I looked at what you already run',
      body: 'Your org graph shows a production tracker, a render farm, a dailies review system and a crew roster connected, across London, Mumbai, Vancouver and Sydney. Governance marks the roster read-only with no write path, so I will read load from it and never write to it.',
      sources: ['Org graph', 'Governance intelligence'],
      delay: 900,
    },
    {
      do: 'ask',
      id: 'starts',
      prompt: 'Which handovers should it check automatically?',
      options: [
        { id: 'hero', label: 'Hero sequences only' },
        { id: 'cross', label: 'Every cross-site handover (recommended)' },
        { id: 'all', label: 'Every handover, including within a site' },
      ],
      delay: 600,
    },
    {
      do: 'branch',
      cases: {
        hero: [
          {
            do: 'say',
            text: 'Hero sequences only — a narrow start. I can widen it once you trust the check.',
            delay: 450,
          },
        ],
        all: [
          {
            do: 'say',
            text: 'Everything, including within a site — that is a lot of checks for handovers that never cross a timezone. You can narrow it per site later.',
            delay: 450,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Every cross-site handover — the right default, because the gap only hurts when nobody is awake to see it. A pre-shift sweep starts it, and so does a publish marked complete while a cache is still writing.',
          delay: 450,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P('TRIGGERS'),
      summary: 'Added 2 triggers — 06:30 London pre-shift sweep · incomplete-cache threshold',
      delay: 650,
    },
    {
      do: 'askMulti',
      id: 'systems',
      prompt:
        'Which systems should this process touch? I have pre-selected everything your org graph shows in use today.',
      options: [
        { id: 'prodtracker', label: 'Production Tracker' },
        { id: 'renderfarm', label: 'Render Farm' },
        { id: 'dailies', label: 'Dailies' },
        { id: 'roster', label: 'Crew Roster' },
      ],
      preselect: ['prodtracker', 'renderfarm', 'dailies', 'roster'],
      delay: 550,
    },
    {
      do: 'say',
      text: 'Wired in. The tracker gives me the checklist and the shot list, the farm tells me whether a cache actually finished writing, dailies gives me the client review date, and the roster tells me who has room. Only the tracker gets a write path, and only behind a gate.',
      delay: 550,
    },
    {
      do: 'canvas',
      patch: P('STAGE1'),
      summary: 'Stage 1 · Verify the handover — 4 activities',
      delay: 750,
    },
    {
      do: 'say',
      text: 'Note the shape of that stage: two reads, then a deterministic diff, then one judgement. The diff is a script because a manifest comparison should give the same answer every time. Naming which shots are actually broken is a judgement, so that one is an agent.',
      delay: 800,
    },
    {
      do: 'canvas',
      patch: P('STAGE2'),
      summary: 'Stage 2 · Size the damage — hours lost vs preventable · milestone M1',
      delay: 800,
    },
    {
      do: 'insight',
      title: 'Found an existing automation',
      body: 'You already have a scheduled job that ticks the handover checklist and emails the receiving site. It checks that publish ran — not that it finished — which is why a truncated cache passes it. I can keep it as the notification step and put the real verification in front of it.',
      sources: ['Scheduled job scan'],
      delay: 900,
    },
    {
      do: 'ask',
      id: 'reuse',
      prompt: 'Keep the existing checklist job as the notification step?',
      options: [
        { id: 'yes', label: 'Yes, keep it' },
        { id: 'replace', label: 'No, replace it entirely' },
      ],
      delay: 500,
    },
    {
      do: 'branch',
      cases: {
        replace: [
          {
            do: 'say',
            text: 'Replacing it — I will own the notification too. (For today I will still wire the existing job so the run has something to call.)',
            delay: 550,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Keeping it. It stays the thing that tells Mumbai the work is ready — it just no longer decides whether that is true.',
          delay: 550,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P('STAGE3'),
      summary: 'Stage 3 · Decide & re-cut — tracker re-cut and cache re-publish wired in',
      delay: 800,
    },
    {
      do: 'askText',
      id: 'approver',
      prompt: 'Who can approve moving shots between sites?',
      placeholder: 'e.g. the Production Managers group',
      delay: 650,
    },
    {
      do: 'say',
      text: 'Done — that group owns the re-cut gate. A coordinator can rebuild a plan, but moving work to another site waits for a Production Manager. The approval is required, and it produces a decision: approve_recut. The tracker re-cut is guarded on it — it cannot run until approve_recut = true. Watch the guard appear.',
      delay: 600,
    },
    {
      do: 'canvas',
      patch: P('APPROVAL'),
      summary: 'Approval gate added · tracker re-cut guarded on approve_recut = true',
      delay: 750,
    },
    {
      do: 'say',
      text: 'And notice what I did not gate. Re-publishing a truncated cache runs on its own, because it spends machine time. Moving a comp spends somebody’s week. Only the second one needs a person, and that asymmetry is the design rather than an oversight.',
      delay: 700,
    },
    {
      do: 'ask',
      id: 'auto',
      prompt: 'When a re-cut stays inside one site — auto-apply it, or always ask?',
      options: [
        { id: 'always', label: 'Always ask a Production Manager (recommended)' },
        { id: 'within', label: 'Auto-apply within a site, ask for cross-site' },
      ],
      delay: 550,
    },
    {
      do: 'branch',
      cases: {
        within: [
          {
            do: 'say',
            text: 'Within-site re-cuts apply automatically, cross-site always asks. You can tighten this per show later.',
            delay: 500,
          },
        ],
      },
      else: [
        {
          do: 'say',
          text: 'Always ask — for now. Every re-cut routes to a human, and you can loosen it once the numbers earn it.',
          delay: 500,
        },
      ],
    },
    {
      do: 'canvas',
      patch: P('STAGE4'),
      summary: 'Stage 4 · Close & compound — client note drafted, lesson captured · milestone M2',
      delay: 750,
    },
    {
      do: 'say',
      text: 'That is the whole process: 4 stages, 15 activities, 2 triggers, 2 milestones, driven by @Handover Control. One human decision in it, and the client note is drafted by a step that holds no send scope. Testing the components now — access and credentials…',
      delay: 900,
    },
    {
      do: 'test',
      title: 'Testing components…',
      items: [
        { label: 'Pre-shift sweep trigger', sub: '06:30 London fires & maps the sequence' },
        { label: 'Production Tracker · read', sub: 'Checklist, shot list and manifest map' },
        { label: 'Render Farm · cache state', sub: 'Write completion readable per version' },
        { label: 'Manifest diff script', sub: 'Same input gives the same answer twice' },
        { label: 'Crew Load Copilot handshake', sub: 'Committed load over A2A, read-only' },
        { label: 'Approval gate', sub: 'Production Managers routing & decision output' },
        { label: 'Re-cut guard', sub: 'Tracker write resolves on approve_recut = true' },
        { label: 'Ungated re-publish', sub: 'Cache re-publish runs without an approval' },
        { label: 'Client-send scope', sub: 'No step holds one — confirmed not grantable' },
      ],
      result: 'All components passed',
      delay: 600,
    },
    {
      do: 'say',
      text: 'All checks passed, including the last one — there is no client-send scope on this process to get wrong. Ready to deploy.',
      delay: 6800,
    },
    {
      do: 'ask',
      id: 'deploy',
      prompt: 'Ready to deploy?',
      options: [
        { id: 'deploy', label: 'Deploy it' },
        { id: 'later', label: 'Not yet' },
      ],
      delay: 600,
    },
    {
      do: 'branch',
      cases: {
        later: [
          {
            do: 'say',
            text: 'Standing by — it is all assembled and tested. Deploy whenever you are ready.',
            delay: 500,
          },
        ],
      },
      else: [
        {
          do: 'deployProcess',
          process: loadAsset,
          summary: 'Cross-Site Handover Control deployed — v2 live',
          delay: 900,
        },
        {
          do: 'say',
          text: 'Deployed. It is live and already listening — connected to time-to-catch, artist-hours lost, first-pass clean rate and artists over their ceiling.',
          delay: 600,
        },
        {
          do: 'link',
          label: 'Open Cross-Site Handover Control — process details',
          href: '/processes/acme-pr04',
          delay: 700,
        },
      ],
    },
  ],
};
